import { cn } from "@/lib/utils";
import { motion } from "motion/react";

export interface ReportProgressProps {
  /** Overall progress from 0 to 1 */
  progress: number;
  /** Current step number */
  currentStep?: number;
  /** Total number of steps */
  totalSteps?: number;
  /** Main status title (e.g., "Generating report...") */
  title?: string;
  /** Sub-status text (e.g., "Finishing...") */
  subStatus?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Additional class names */
  className?: string;
}

const sizeConfig = {
  sm: {
    ring: 64,
    stroke: 5,
    percentText: "text-base",
    stepsText: "text-xs",
    titleText: "text-base",
    subStatusText: "text-sm",
    gap: "gap-4",
  },
  md: {
    ring: 80,
    stroke: 6,
    percentText: "text-xl",
    stepsText: "text-xs",
    titleText: "text-lg",
    subStatusText: "text-sm",
    gap: "gap-5",
  },
  lg: {
    ring: 96,
    stroke: 7,
    percentText: "text-2xl",
    stepsText: "text-sm",
    titleText: "text-xl",
    subStatusText: "text-base",
    gap: "gap-6",
  },
};

function CircularProgress({
  progress,
  currentStep,
  totalSteps,
  size = "md",
}: {
  progress: number;
  currentStep?: number;
  totalSteps?: number;
  size?: "sm" | "md" | "lg";
}) {
  const config = sizeConfig[size];
  const radius = (config.ring - config.stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - progress * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Background ring */}
      <svg
        width={config.ring}
        height={config.ring}
        className="transform -rotate-90"
        aria-label={`Progress: ${Math.round(progress * 100)}%`}
        role="img"
      >
        <circle
          cx={config.ring / 2}
          cy={config.ring / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          className="text-gray-100"
        />
        {/* Progress ring */}
        <motion.circle
          cx={config.ring / 2}
          cy={config.ring / 2}
          r={radius}
          fill="none"
          stroke="#7c3aed"
          strokeWidth={config.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className={cn("font-semibold text-violet-600", config.percentText)}
          key={Math.round(progress * 100)}
          initial={{ scale: 1.1, opacity: 0.7 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {Math.round(progress * 100)}%
        </motion.span>
        {currentStep !== undefined && totalSteps !== undefined && (
          <span className={cn("text-gray-500", config.stepsText)}>
            {currentStep} of {totalSteps}
          </span>
        )}
      </div>
    </div>
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex items-center ml-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="text-violet-600"
          animate={{
            opacity: [0.2, 1, 0.2],
          }}
          transition={{
            duration: 1.2,
            repeat: Number.POSITIVE_INFINITY,
            delay: i * 0.3,
            ease: "easeInOut",
          }}
        >
          .
        </motion.span>
      ))}
    </span>
  );
}

export default function ReportProgress({
  progress,
  currentStep,
  totalSteps,
  title = "Generating report",
  subStatus,
  size = "md",
  className,
}: ReportProgressProps) {
  const config = sizeConfig[size];

  return (
    <div className={cn("flex items-center", config.gap, className)}>
      {/* Circular progress */}
      <CircularProgress
        progress={progress}
        currentStep={currentStep}
        totalSteps={totalSteps}
        size={size}
      />

      {/* Title and sub-status */}
      <div className="space-y-0.5">
        <motion.p
          className={cn("font-semibold text-violet-600", config.titleText)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {title}
          <AnimatedDots />
        </motion.p>

        {subStatus && (
          <p className={cn("text-gray-500", config.subStatusText)}>
            {subStatus}
          </p>
        )}
      </div>
    </div>
  );
}
